import { useEffect, useRef } from 'react';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import { useFanLetterDetail } from './hooks/useFanLetterDetail';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import FanLetterCardHeader from './components/FanLetterCardHeader';
import FanLetterLockOverlay from './components/FanLetterLockOverlay';
import FanLetterFooter from './components/FanLetterFooter';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import { BettieLetterStyle } from '../../constants/BettieGradientColor';

const BettieLetterDetailPage = () => {
  const { colorMode } = useColorMode();
  const handleHistoryBack = useHandleHistoryBack();
  const { fanLetter, handleOpenFanLetter, isLoadingFanLetter, isLoadingGenerate } =
    useFanLetterDetail();

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAccumulator = useRef(0);
  const animationIdRef = useRef<number | null>(null);

  const isFanLetterOpen = fanLetter?.seenAt !== null;

  const stopAutoScroll = () => {
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: stopAutoScroll is not a dependency
  useEffect(() => {
    if (!isFanLetterOpen) return;
    const container = containerRef.current;
    if (!container) return;

    container.scrollTop = 0;

    scrollAccumulator.current = 0;

    const animateScroll = () => {
      if (container.scrollTop < container.scrollHeight - container.clientHeight) {
        scrollAccumulator.current += 0.2;
        container.scrollTop = scrollAccumulator.current;

        animationIdRef.current = requestAnimationFrame(animateScroll);
      } else {
        stopAutoScroll();
      }
    };

    animationIdRef.current = requestAnimationFrame(animateScroll);

    return () => stopAutoScroll();
  }, [isFanLetterOpen, fanLetter?.content]);


  if (isLoadingFanLetter) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }
  
  if (!fanLetter) {
    return null;
  }

  return (
    <div
      className="min-h-screen pt-[calc(env(safe-area-inset-top)+55px)] pb-[env(safe-area-inset-bottom)] fixed inset-0"
      style={{
        backgroundImage: `url(/assets/bettie/bettie_letter_bg_${colorMode}.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="fixed top-0 left-0 px-[16px] pb-[10px] pt-[calc(10px+env(safe-area-inset-top))]">
        <Xmark size={24} className="text-text-main-lv1" onClick={handleHistoryBack} />
      </div>
      <div className="px-[24px] flex flex-col items-center gap-[22px] mt-[23px]">
        {isFanLetterOpen ? (
          <img src="/assets/logos/fanhearts_logo.svg" alt="fanhearts" />
        ) : (
          <div className="h-[13px]" /> // 레이아웃 유지를 위해 빈 요소 추가
        )}
        <div
          className="px-[18px] pt-[19px] rounded-[20px] min-w-[327px] border border-[rgba(255,255,255,0.50)]"
          style={BettieLetterStyle.fanLetterCard[colorMode]}
        >
          <FanLetterCardHeader fanLetter={fanLetter} />
          <div className="relative w-full h-[322px]">
            {isFanLetterOpen && fanLetter.content && fanLetter.content.length > 0 && (
              <section
                ref={containerRef}
                className="h-full overflow-y-scroll"
                style={{
                  overflowY: isFanLetterOpen ? 'scroll' : 'hidden',
                  ...BettieLetterStyle.fanLetterMaskImage
                }}
                aria-label="fan letter content scroll area"
                onTouchStart={stopAutoScroll}
                onWheel={stopAutoScroll}
                onMouseDown={stopAutoScroll}
              >
                <p
                  className="text-[15px] leading-[27px] text-text-main-lv2 whitespace-pre-line px-[10px]"
                  style={BettieLetterStyle.profileBorder}
                >
                  {fanLetter.content}
                </p>
              </section>
            )}
            {!isFanLetterOpen &&
              (isLoadingGenerate ? (
                <LoadingFanLetter />
              ) : (
                <FanLetterLockOverlay fanLetter={fanLetter} />
              ))}
          </div>
          {isLoadingGenerate ? (
            <div className="h-[100px]" /> // 레이아웃 유지를 위해 빈 요소 추가
          ) : (
            <FanLetterFooter
              isFanLetterOpen={isFanLetterOpen}
              fanLetter={fanLetter}
              handleOpenFanLetter={handleOpenFanLetter}
            />
          )}
        </div>
      </div>
      {isFanLetterOpen && (
        <div className="fixed bottom-[calc(10px+env(safe-area-inset-bottom))] left-0 right-0 px-[24px]">
          <Button
            size="large"
            btnColor="secondary"
            className="w-full text-[17px] font-semibold"
            onClick={handleHistoryBack}
          >
            {t('confirm')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BettieLetterDetailPage;


const LoadingFanLetter = () => {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
      <GradientLoadingSpinner size={36} />
      <p className="text-[17px] font-semibold text-text-main-lv2 leading-[20px] mt-[24px]">
        {t('BettieLetterDetail.loadLetter')}
      </p>
      <p className="text-[15px] font-medium text-text-main-lv3 leading-[18px] mt-[8px]">
        {t('BettieLetterDetail.waitForSeconds')}
      </p>
    </div>
  );
};